/**
 * Compresión client-side de imágenes (JPEG/PNG/WebP) antes de subirlas a
 * Firebase Storage. Reduce ancho de banda en móvil y baja la presión sobre
 * el pre-analyzer del backend (imágenes más chicas = menos sharp processing).
 *
 * Estrategia:
 *  1. Si el archivo no es imagen o ya pesa menos de COMPRESS_THRESHOLD → devolver tal cual.
 *  2. Cargar como `Image`, dibujar en `<canvas>` con downscale proporcional
 *     hasta que el lado mayor quede ≤ MAX_DIMENSION_PX.
 *  3. Exportar como JPEG con calidad JPEG_QUALITY.
 *  4. Si el output sale más grande que el original (raro pero posible con
 *     PNGs muy chicos), devolver el original.
 *  5. Si algo falla (canvas no disponible, decode error, etc.) → devolver el
 *     archivo original. Cero downside: el flujo sigue funcionando.
 *
 * Mantenemos el `File.name` original (UX) y actualizamos el MIME a image/jpeg
 * en el resultado (sin tocar el original que el caller pase).
 */

// Target lógico documentado: 1.5MB. El cap real lo decide el blob.size
// resultante de canvas.toBlob con JPEG_QUALITY + MAX_DIMENSION_PX.
const COMPRESS_THRESHOLD_BYTES = 2 * 1024 * 1024; // No comprimir < 2MB
const MAX_DIMENSION_PX = 2400; // Lado mayor cap: 2400px conserva detalle de menús
const JPEG_QUALITY = 0.82;

export interface CompressionResult {
  file: File;
  /** true si se aplicó compresión; false si se devolvió el original. */
  compressed: boolean;
  /** Tamaño original en bytes (siempre presente). */
  originalBytes: number;
  /** Tamaño final en bytes (== originalBytes si compressed=false). */
  finalBytes: number;
}

const IMAGE_MIMES = new Set(['image/jpeg', 'image/png', 'image/webp']);

/**
 * Renombra `foo.png` → `foo.jpeg`, `foo.PNG` → `foo.jpeg`, sin extensión → `foo.jpeg`.
 * Mantiene la base intacta y solo toca la extensión final.
 *
 * @param {string} name Nombre original del archivo.
 * @returns {string} Nombre con extensión `.jpeg`.
 */
export function renameToJpeg(name: string): string {
  const dot = name.lastIndexOf('.');
  if (dot <= 0) return `${name}.jpeg`;
  return `${name.slice(0, dot)}.jpeg`;
}

/**
 * Comprime una imagen a JPEG con cap MAX_DIMENSION_PX en lado mayor y calidad
 * JPEG_QUALITY. Si no es imagen, ya está por debajo del umbral, o el entorno
 * no soporta canvas, devuelve el original sin tocarlo. Nunca lanza — en caso
 * de error devuelve el original con `compressed=false`.
 *
 * @param {File} file Archivo subido por el usuario (mime debe ser image/* para activar compresión).
 * @returns {Promise<CompressionResult>} Resultado con `file` final, `compressed` flag y tamaños.
 */
export async function compressImage(file: File): Promise<CompressionResult> {
  const originalBytes = file.size;

  if (!IMAGE_MIMES.has(file.type)) {
    return { file, compressed: false, originalBytes, finalBytes: originalBytes };
  }
  if (originalBytes <= COMPRESS_THRESHOLD_BYTES) {
    return { file, compressed: false, originalBytes, finalBytes: originalBytes };
  }
  // Verifica que el entorno soporte canvas + createImageBitmap. En SSR
  // o entornos jsdom esto no aplica y devolvemos el original.
  if (typeof document === 'undefined' || typeof createImageBitmap !== 'function') {
    return { file, compressed: false, originalBytes, finalBytes: originalBytes };
  }

  try {
    const bitmap = await createImageBitmap(file);
    try {
      const { width: srcW, height: srcH } = bitmap;
      const longSide = Math.max(srcW, srcH);
      const scale = longSide > MAX_DIMENSION_PX ? MAX_DIMENSION_PX / longSide : 1;
      const dstW = Math.round(srcW * scale);
      const dstH = Math.round(srcH * scale);

      const canvas = document.createElement('canvas');
      canvas.width = dstW;
      canvas.height = dstH;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return { file, compressed: false, originalBytes, finalBytes: originalBytes };
      }
      ctx.drawImage(bitmap, 0, 0, dstW, dstH);

      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob((b) => resolve(b), 'image/jpeg', JPEG_QUALITY);
      });
      if (!blob || blob.size === 0) {
        return { file, compressed: false, originalBytes, finalBytes: originalBytes };
      }
      // Si el output salió MÁS grande que el original (raro pero posible con
      // PNGs o JPEGs ya muy comprimidos), devolver el original.
      if (blob.size >= originalBytes) {
        return { file, compressed: false, originalBytes, finalBytes: originalBytes };
      }

      const compressedName = renameToJpeg(file.name);
      const compressedFile = new File([blob], compressedName, {
        type: 'image/jpeg',
        lastModified: file.lastModified,
      });
      return {
        file: compressedFile,
        compressed: true,
        originalBytes,
        finalBytes: blob.size,
      };
    } finally {
      // Liberar memoria del bitmap (importante en móviles).
      if (typeof bitmap.close === 'function') bitmap.close();
    }
  } catch {
    return { file, compressed: false, originalBytes, finalBytes: originalBytes };
  }
}

/**
 * Comprime un array de archivos en paralelo, preservando el orden de salida
 * para que el caller pueda matchear con el input original por index.
 *
 * @param {File[]} files Archivos a comprimir (PDFs e imágenes <2MB pasan tal cual).
 * @returns {Promise<CompressionResult[]>} Resultados en el mismo orden del input.
 */
export async function compressImages(files: File[]): Promise<CompressionResult[]> {
  return Promise.all(files.map(compressImage));
}
